// Import React FIRST and make it globally available IMMEDIATELY
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';

// Make React globally available BEFORE any other imports
// This ensures hoist-non-react-statics and QueryClientProvider can find React
if (typeof window !== 'undefined') {
    window.React = React;
    window.ReactDOM = ReactDOM;
    // Also add React.Component for class components
    window.Component = React.Component;
    window.PureComponent = React.PureComponent;
}
// Also make React available on globalThis for module compatibility
// eslint-disable-next-line no-undef
if (typeof globalThis !== 'undefined') {
    // eslint-disable-next-line no-undef
    globalThis.React = React;
    // eslint-disable-next-line no-undef
    globalThis.ReactDOM = ReactDOM;
    // eslint-disable-next-line no-undef
    globalThis.Component = React.Component;
    // eslint-disable-next-line no-undef
    globalThis.PureComponent = React.PureComponent;
}

// Now import everything else AFTER React is globally available
import { Provider } from 'react-redux';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { TrackJS } from 'trackjs';
import config from './config';
import { store } from './store';
import './guest/clearStaleUserOnLand';
import { createQueryClient } from './query/client';
import { initializeGlobalQueryClient } from './hooks/useConfiguration';
import { QueryClientProvider } from '@tanstack/react-query';
import AppWrapper from './AppWrapper';
import { appConfig } from './config/app.config';
import { mixpanelTrackingEvent } from './utils/mixpanelTrackingEventHandler';
import { silenceNonAppScriptErrors } from '@intigral/utils/lib/datadog';

if (typeof window !== 'undefined') {
    mixpanelTrackingEvent.listen();
    silenceNonAppScriptErrors();
}

if (config.trackJs !== undefined && config.trackJs.token !== undefined) {
    TrackJS.install({
        token: config.trackJs.token,
        application: config.trackJs.application,
        version: config.trackJs.versionPrefix + config.build.version,
    });
    TrackJS.addMetadata('Portal', config.trackJs.portal);
    TrackJS.addMetadata('Environment', config.trackJs.environment);
}

const appRootElement = document.getElementById('root');
const root = createRoot(appRootElement || document.children[0]);

/**
 * Schedules a one-time tracking pixel request after critical startup work.
 *
 * Strategy:
 * - Wait for the window load event when initial resources are still loading.
 * - Use requestIdleCallback when available so the beacon runs during idle time.
 * - Fall back to setTimeout(0) for browsers without requestIdleCallback.
 *
 * This keeps analytics tracking and avoids slowing down the first render.
 */
const schedulePixelTrackingBeacon = () => {
    const beaconUrl = 'https://d21y75miwcfqoq.cloudfront.net/066b5a7a';

    const sendBeacon = () => {
        const beaconImage = new Image();
        beaconImage.src = beaconUrl;
    };

    const runWhenIdle = () => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(sendBeacon, { timeout: 5000 });
            return;
        }

        window.setTimeout(sendBeacon, 0);
    };

    if (document.readyState === 'complete') {
        runWhenIdle();
        return;
    }

    window.addEventListener('load', runWhenIdle, { once: true });
};

const Main = () => {
    const queryClient = createQueryClient();

    // Initialize global query client for configuration access
    initializeGlobalQueryClient(queryClient);

    const isQueryDevToolsEnable = appConfig?.enableReactQueryDevTool === 'true';

    const ReactQueryDevtoolsProduction = React.lazy(() =>
        import('@tanstack/react-query-devtools/production').then((d) => ({
            default: d.ReactQueryDevtools,
        }))
    );

    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <AppWrapper />
            </Provider>
            {isQueryDevToolsEnable && (
                <>
                    <React.Suspense fallback={null}>
                        <ReactQueryDevtoolsProduction initialIsOpen={false} />
                    </React.Suspense>
                </>
            )}
        </QueryClientProvider>
    );
};

root.render(<Main />);
schedulePixelTrackingBeacon();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

reportWebVitals();
